import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatDate } from "../../lib/api";
import { Heart, Save } from "lucide-react";
import { toast } from "sonner";

export default function FundsTab() {
  const [amount, setAmount] = useState("");
  const [cause, setCause] = useState("");
  const [donateUrl, setDonateUrl] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/funds");
      setAmount(String(data.amount));
      setCause(data.cause || "");
      setDonateUrl(data.donate_url || "");
      setUpdatedAt(data.updated_at);
    } catch {
      toast.error("Failed to load funds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/funds", {
        amount: parseFloat(amount),
        cause: cause || undefined,
        donate_url: donateUrl,
      });
      setUpdatedAt(data.updated_at);
      toast.success(`Funds raised updated to ${formatCurrency(data.amount)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="funds-tab">
      <header className="mb-8">
        <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
          handmade for a cause
        </p>
        <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
          Funds Raised
        </h1>
      </header>

      <div
        className="bg-white rounded-2xl p-6 md:p-8 max-w-xl"
        style={{ border: "1px solid var(--border-soft)" }}
      >
        {loading ? (
          <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>
            loading…
          </p>
        ) : (
          <form onSubmit={save} className="space-y-5">
            <div className="flex items-center gap-3 mb-2">
              <Heart size={20} fill="currentColor" style={{ color: "var(--brand-rose)" }} />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--text-secondary)" }}>
                Public homepage figure
              </span>
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">Amount raised (USD)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display text-2xl" style={{ color: "var(--brand-sage)" }}>
                  $
                </span>
                <input
                  type="number"
                  step="1"
                  min="0"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl outline-none text-2xl font-display"
                  style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border-soft)", color: "var(--text-primary)" }}
                  data-testid="funds-amount-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">Cause description (optional)</label>
              <textarea
                rows={3}
                value={cause}
                onChange={(e) => setCause(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl outline-none resize-none"
                style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border-soft)" }}
                placeholder="All profits purchase gifts for children at the hospital."
                data-testid="funds-cause-input"
              />
            </div>

            <div>
              <label className="text-sm font-semibold block mb-1.5">
                PayPal donate URL
                <span className="ml-2 font-normal text-xs" style={{ color: "var(--text-secondary)" }}>
                  (leave blank to hide the Donate button on the homepage)
                </span>
              </label>
              <input
                type="url"
                value={donateUrl}
                onChange={(e) => setDonateUrl(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl outline-none"
                style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border-soft)" }}
                placeholder="https://www.paypal.com/paypalme/yourusername"
                data-testid="funds-donate-url-input"
              />
            </div>

            {updatedAt && (
              <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                Last updated: {formatDate(updatedAt)}
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="btn-sage inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
              data-testid="save-funds-button"
            >
              <Save size={16} />
              {saving ? "Saving…" : "Save"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
